/**
 * Phase1103 / Phase1104 — Order Status Semantic Compatibility Layer
 *
 * Purpose: Map legacy orders.status values to canonical states, events,
 * notification eligibility, and pickup governance state — WITHOUT changing
 * the DB schema or orders.status enum.
 *
 * Key rules:
 *   approved = accepted (NOT ready_for_pickup)
 *   renting  = rental_active (post-handoff, contract active, risk transferred)
 *   completed = returned_or_completed (NOT legal closure)
 */

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type LegacyOrderStatus =
  | 'draft'
  | 'pending'
  | 'need_info'
  | 'approved'
  | 'renting'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | string;

export type CanonicalOrderState =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'waiting_customer_supplement'
  | 'accepted'
  | 'ready_for_pickup'
  | 'picked_up'
  | 'rental_active'
  | 'returned'
  | 'returned_or_completed'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'disputed'
  | 'legal_hold'
  | 'unknown';

export type CanonicalOrderEvent =
  | 'order.submitted'
  | 'order.accepted'
  | 'order.ready_for_pickup'
  | 'order.picked_up'
  | 'order.returned'
  | 'order.completed'
  | 'order.cancelled'
  | 'order.rejected'
  | null;

export type PickupGovernanceState =
  | 'pre_contract'
  | 'pre_handoff'
  | 'ready_not_picked_up'
  | 'post_handoff_active'
  | 'return_or_completion_pending'
  | 'terminal_pre_handoff'
  | 'terminal_normal'
  | 'legal_review'
  | 'unknown';

export interface ReadyForPickupCompatibility {
  explicit_ready_for_pickup: boolean;
  source: 'manual_action' | 'runtime_event_outbox' | 'future_substatus' | 'future_ready_for_pickup_at';
  ready_for_pickup_at?: string | null;
  actor_id?: string | null;
  audit_ref?: string | null;
}

export interface NotificationEligibilityResult {
  eligible: boolean;
  event_type: CanonicalOrderEvent;
  deny_reason?: string;
  template_key?: string;
}

// ─────────────────────────────────────────────
// Canonical template key mapping
// ─────────────────────────────────────────────

const TEMPLATE_KEYS: Partial<Record<NonNullable<CanonicalOrderEvent>, string>> = {
  'order.submitted':        'customer.order_status.submitted.v1',
  'order.accepted':         'customer.order_status.accepted.v1',
  'order.ready_for_pickup': 'customer.order_status.ready_for_pickup.v1',
  'order.picked_up':        'customer.order_status.picked_up.v1',
  'order.returned':         'customer.order_status.returned.v1',
  'order.cancelled':        'customer.order_status.cancelled.v1',
  'order.rejected':         'customer.order_status.rejected.v1',
};

// ─────────────────────────────────────────────
// 1. getCanonicalOrderState
// ─────────────────────────────────────────────

/**
 * Maps legacy orders.status to a canonical semantic state.
 *
 * CRITICAL: approved maps to 'accepted', NOT 'ready_for_pickup'.
 * ready_for_pickup requires an explicit action/signal (never inferred from approved).
 */
export function getCanonicalOrderState(input: {
  legacy_status: LegacyOrderStatus;
  ready_for_pickup?: ReadyForPickupCompatibility | null;
  previous_legacy_status?: LegacyOrderStatus | null;
}): CanonicalOrderState {
  const { legacy_status, ready_for_pickup } = input;

  switch (legacy_status) {
    case 'draft':
      return 'draft';

    case 'pending':
      return 'submitted';

    case 'need_info':
      return 'under_review';

    case 'approved':
      // ⚠️ CRITICAL: approved = accepted, NEVER ready_for_pickup
      // ready_for_pickup requires an explicit future signal
      if (ready_for_pickup?.explicit_ready_for_pickup) return 'ready_for_pickup';
      return 'accepted';

    case 'renting':
      // renting = post-handoff, rental active, custody transferred
      return 'rental_active';

    case 'completed':
      // completed = returned/completed legacy state, NOT legal closure
      return 'returned_or_completed';

    case 'cancelled':
      return 'cancelled';

    case 'rejected':
      return 'rejected';

    default:
      return 'unknown';
  }
}

// ─────────────────────────────────────────────
// 2. getCanonicalOrderEvent
// ─────────────────────────────────────────────

/**
 * Returns the canonical event candidate for a given status/transition.
 *
 * Events are transition-bound — they apply on status change, not on current state view.
 */
export function getCanonicalOrderEvent(input: {
  legacy_status: LegacyOrderStatus;
  previous_legacy_status?: LegacyOrderStatus | null;
  ready_for_pickup?: ReadyForPickupCompatibility | null;
  event_source?: 'status_transition' | 'explicit_action' | 'order_created';
}): CanonicalOrderEvent {
  const { legacy_status, previous_legacy_status, ready_for_pickup, event_source } = input;

  switch (legacy_status) {
    case 'pending':
      // Only on creation or first transition into pending
      if (!previous_legacy_status || previous_legacy_status === 'draft' || event_source === 'order_created') {
        return 'order.submitted';
      }
      return null;

    case 'approved':
      // accepted event — transition into approved
      if (previous_legacy_status && previous_legacy_status !== 'approved') {
        return 'order.accepted';
      }
      return null;

    case 'renting':
      // picked_up event — transition into renting
      if (previous_legacy_status && previous_legacy_status !== 'renting') {
        return 'order.picked_up';
      }
      return null;

    case 'completed':
      // returned event — transition into completed
      if (previous_legacy_status && previous_legacy_status !== 'completed') {
        return 'order.returned';
      }
      return null;

    case 'cancelled':
      // cancelled event — only safe if pre-handoff
      if (previous_legacy_status && !['renting', 'completed'].includes(previous_legacy_status)) {
        return 'order.cancelled';
      }
      return null;

    case 'rejected':
      if (previous_legacy_status && previous_legacy_status !== 'rejected') {
        return 'order.rejected';
      }
      return null;

    default:
      // ready_for_pickup requires explicit action, not status-based inference
      if (ready_for_pickup?.explicit_ready_for_pickup && event_source === 'explicit_action') {
        return 'order.ready_for_pickup';
      }
      return null;
  }
}

// ─────────────────────────────────────────────
// 3. getCustomerNotificationEligibility
// ─────────────────────────────────────────────

/**
 * Returns whether a customer notification is eligible for a given state/event combo.
 *
 * Rules:
 * - approved can only produce order.accepted, NEVER order.ready_for_pickup
 * - order.ready_for_pickup requires an EXPLICIT ready signal
 * - order.cancelled is only eligible if previous state was pre-handoff
 */
export function getCustomerNotificationEligibility(input: {
  legacy_status: LegacyOrderStatus;
  canonical_state: CanonicalOrderState;
  canonical_event: CanonicalOrderEvent;
  previous_legacy_status?: LegacyOrderStatus | null;
  ready_for_pickup?: ReadyForPickupCompatibility | null;
}): NotificationEligibilityResult {
  const { canonical_event, legacy_status, canonical_state, previous_legacy_status, ready_for_pickup } = input;

  if (!canonical_event) {
    return {
      eligible: false,
      event_type: null,
      deny_reason: 'no_event_candidate',
    };
  }

  switch (canonical_event) {
    case 'order.submitted':
      return {
        eligible: canonical_state === 'submitted',
        event_type: canonical_event,
        deny_reason: canonical_state !== 'submitted' ? 'not_submission_event' : undefined,
        template_key: TEMPLATE_KEYS['order.submitted'],
      };

    case 'order.accepted':
      // approved → accepted ONLY
      return {
        eligible: legacy_status === 'approved',
        event_type: canonical_event,
        deny_reason: legacy_status !== 'approved' ? 'not_acceptance_event' : undefined,
        template_key: TEMPLATE_KEYS['order.accepted'],
      };

    case 'order.ready_for_pickup':
      // MUST be explicit — never inferred from approved
      if (!ready_for_pickup?.explicit_ready_for_pickup) {
        return {
          eligible: false,
          event_type: canonical_event,
          deny_reason: 'ready_for_pickup_not_explicit',
        };
      }
      return {
        eligible: true,
        event_type: canonical_event,
        template_key: TEMPLATE_KEYS['order.ready_for_pickup'],
      };

    case 'order.picked_up':
      return {
        eligible: legacy_status === 'renting',
        event_type: canonical_event,
        deny_reason: legacy_status !== 'renting' ? 'not_picked_up_transition' : undefined,
        template_key: TEMPLATE_KEYS['order.picked_up'],
      };

    case 'order.returned':
      return {
        eligible: legacy_status === 'completed',
        event_type: canonical_event,
        deny_reason: legacy_status !== 'completed' ? 'not_return_event' : undefined,
        template_key: TEMPLATE_KEYS['order.returned'],
      };

    case 'order.cancelled':
      // Only eligible if previous state was pre-handoff (not after renting)
      if (previous_legacy_status && ['renting', 'completed'].includes(previous_legacy_status)) {
        return {
          eligible: false,
          event_type: canonical_event,
          deny_reason: 'cancelled_after_pickup_not_allowed',
        };
      }
      return {
        eligible: legacy_status === 'cancelled',
        event_type: canonical_event,
        deny_reason: legacy_status !== 'cancelled' ? 'not_cancellation_event' : undefined,
        template_key: TEMPLATE_KEYS['order.cancelled'],
      };

    case 'order.rejected':
      return {
        eligible: legacy_status === 'rejected',
        event_type: canonical_event,
        deny_reason: legacy_status !== 'rejected' ? 'not_rejection_event' : undefined,
        template_key: TEMPLATE_KEYS['order.rejected'],
      };

    default:
      return {
        eligible: false,
        event_type: null,
        deny_reason: 'unknown_event',
      };
  }
}

// ─────────────────────────────────────────────
// 4. getPickupGovernanceState
// ─────────────────────────────────────────────

/**
 * Returns the pickup governance lifecycle state.
 *
 * approved stays pre_handoff — it does NOT mean the equipment is ready to hand off.
 * renting = post_handoff_active (contract active, custody transferred).
 */
export function getPickupGovernanceState(input: {
  legacy_status: LegacyOrderStatus;
  canonical_state: CanonicalOrderState;
  ready_for_pickup?: ReadyForPickupCompatibility | null;
}): PickupGovernanceState {
  const { legacy_status, canonical_state, ready_for_pickup } = input;

  switch (legacy_status) {
    case 'draft':
    case 'pending':
    case 'need_info':
      return 'pre_contract';

    case 'approved':
      // approved = pre_handoff. If explicit readiness signal exists, bump to ready_not_picked_up.
      if (ready_for_pickup?.explicit_ready_for_pickup) return 'ready_not_picked_up';
      return 'pre_handoff';

    case 'renting':
      // POST-HANDOFF: contract active, custody transferred
      return 'post_handoff_active';

    case 'completed':
      return 'return_or_completion_pending';

    case 'cancelled':
      return 'terminal_pre_handoff';

    case 'rejected':
      return 'terminal_pre_handoff';

    default:
      return 'unknown';
  }
}

// ─────────────────────────────────────────────
// 5. getOrderStatusDisplay — UI wording helper
// ─────────────────────────────────────────────

export interface OrderStatusDisplay {
  /** Admin label */
  adminLabel: string;
  /** Customer-facing label (zh-TW) */
  customerLabel: string;
  /** Canonical state key */
  canonicalState: CanonicalOrderState;
  /** Semantic warning message (if any) — shown in admin UI */
  semanticWarning?: string;
  /** Whether this status is post-handoff (contract active) */
  isPostHandoff: boolean;
  /** Whether this status is terminal */
  isTerminal: boolean;
  /** Whether approved → ready_for_pickup is explicitly blocked */
  readyForPickupBlocked: boolean;
}

const STATUS_DISPLAY_MAP: Record<string, OrderStatusDisplay> = {
  draft: {
    adminLabel: '草稿',
    customerLabel: '草稿',
    canonicalState: 'draft',
    isPostHandoff: false,
    isTerminal: false,
    readyForPickupBlocked: false,
  },
  pending: {
    adminLabel: '已送出，待門市確認',
    customerLabel: '訂單已送出，門市確認中',
    canonicalState: 'submitted',
    isPostHandoff: false,
    isTerminal: false,
    readyForPickupBlocked: false,
  },
  need_info: {
    adminLabel: '需補充資料 / 審核中',
    customerLabel: '需補充資料，請依門市通知處理',
    canonicalState: 'under_review',
    semanticWarning: '此狀態為審核中或補件，非爭議狀態（除非有明確爭議紀錄）。',
    isPostHandoff: false,
    isTerminal: false,
    readyForPickupBlocked: false,
  },
  approved: {
    adminLabel: '訂單已接受',
    customerLabel: '訂單已接受，請等候可取件通知或依門市指示',
    canonicalState: 'accepted',
    semanticWarning: '訂單已接受，但不代表器材已備妥可取件。可取件需另行明確操作/事件。',
    isPostHandoff: false,
    isTerminal: false,
    readyForPickupBlocked: true,
  },
  renting: {
    adminLabel: '已取件 / 租借中',
    customerLabel: '已取件，租借進行中',
    canonicalState: 'rental_active',
    semanticWarning: '契約生效中，風險已移轉。不可普通取消，回退需店長以上核准。',
    isPostHandoff: true,
    isTerminal: false,
    readyForPickupBlocked: false,
  },
  completed: {
    adminLabel: '已歸還 / 已完成',
    customerLabel: '已歸還 / 已完成',
    canonicalState: 'returned_or_completed',
    semanticWarning: '歸還/完成流程已執行，但法律保全結案需另行確認（檢核、無爭議、無未付款）。',
    isPostHandoff: false,
    isTerminal: true,
    readyForPickupBlocked: false,
  },
  cancelled: {
    adminLabel: '已取消',
    customerLabel: '已取消',
    canonicalState: 'cancelled',
    isPostHandoff: false,
    isTerminal: true,
    readyForPickupBlocked: false,
  },
  rejected: {
    adminLabel: '無法承接',
    customerLabel: '很抱歉，此訂單目前無法承接',
    canonicalState: 'rejected',
    isPostHandoff: false,
    isTerminal: true,
    readyForPickupBlocked: false,
  },
};

export function getOrderStatusDisplay(legacyStatus: LegacyOrderStatus): OrderStatusDisplay {
  return (
    STATUS_DISPLAY_MAP[legacyStatus] ?? {
      adminLabel: legacyStatus,
      customerLabel: legacyStatus,
      canonicalState: 'unknown',
      isPostHandoff: false,
      isTerminal: false,
      readyForPickupBlocked: false,
    }
  );
}

// ─────────────────────────────────────────────
// 6. Utility: isPostHandoffStatus
// ─────────────────────────────────────────────

/** Returns true if the order is in a post-handoff state (contract active, custody transferred) */
export function isPostHandoffStatus(legacyStatus: LegacyOrderStatus): boolean {
  return legacyStatus === 'renting';
}

/** Returns true if the status is a terminal state (no further meaningful transitions) */
export function isTerminalStatus(legacyStatus: LegacyOrderStatus): boolean {
  return ['completed', 'cancelled', 'rejected'].includes(legacyStatus);
}

/**
 * Returns true if ready_for_pickup notification is blocked for this status.
 * This MUST always be true for approved — approved ≠ ready_for_pickup.
 */
export function isReadyForPickupBlocked(legacyStatus: LegacyOrderStatus): boolean {
  return legacyStatus === 'approved';
}